import { parseHypnogramComponent, parseHypnogramSelection, type HypnogramComponent, type HypnogramSelection } from "./assistant-hypnogram";
/** Versioned Neoma presentation data carried by standard AG-UI STATE_SNAPSHOT. */
export type ChartRepresentation = "pie" | "bar";
export interface SleepStageBreakdown {
  schema: "neoma.sleep-stage-breakdown/2"; type: "sleep_stage_breakdown";
  status: "available" | "unavailable"; reason: string | null;
  unit: "percent"; denominator: "total_sleep_time"; total_sleep_minutes: number | null;
  stages: { stage: "N1" | "N2" | "N3" | "REM"; minutes: number; percent: number }[];
  representations: ["pie", "bar"]; initial_representation: "pie"; fallback: string;
}
export interface ChatComponent {
  id: string; conversation_id: string; message_id: string; request_id: string;
  schema: string; payload: SleepStageBreakdown | HypnogramComponent | null;
  state: { representation?: ChartRepresentation; selection?: HypnogramSelection | null; revision: number };
}
function obj(v: unknown): Record<string, unknown> {
  if (!v || typeof v !== "object" || Array.isArray(v)) throw new TypeError("Invalid component");
  return v as Record<string, unknown>;
}
function str(v: unknown): string {
  if (typeof v !== "string" || !v || v.length > 2000) throw new TypeError("Invalid component string");
  return v;
}
function num(v: unknown): number {
  if (typeof v !== "number" || !Number.isFinite(v) || v < 0) throw new TypeError("Invalid component number");
  return v;
}
export function parseComponent(value: unknown): ChatComponent {
  const v = obj(value), p = obj(v.payload), s = obj(v.state);
  if (!Number.isSafeInteger(s.revision) || num(s.revision) > 1e9 + 1) throw new TypeError("Invalid component state");
  const identity = { id: str(v.id), conversation_id: str(v.conversation_id), message_id: str(v.message_id), request_id: str(v.request_id), schema: str(p.schema) };
  if ([identity.id, identity.conversation_id, identity.message_id, identity.request_id].some(id => !/^[a-z0-9]{15}$/.test(id)) ||
    Object.keys(v).some(key => !["id", "conversation_id", "message_id", "request_id", "payload", "state"].includes(key))) throw new TypeError("Invalid component identity");
  if (p.schema === "neoma.hypnogram/2") {
    if (Object.keys(s).some(key => !["selection", "revision"].includes(key))) throw new TypeError("Invalid component state");
    const payload = parseHypnogramComponent(p), selection = parseHypnogramSelection(s.selection);
    if (selection && (!payload.timeline || selection.time_seconds > payload.timeline.duration_seconds || selection.end_seconds > payload.timeline.duration_seconds)) throw new TypeError("Selection exceeds recording");
    return { ...identity, payload, state: { selection, revision: Number(s.revision) } };
  }
  if (p.schema === "neoma.sleep-stage-breakdown/2" && (! ["pie", "bar"].includes(String(s.representation)) || Object.keys(s).some(key => !["representation", "revision"].includes(key)))) throw new TypeError("Invalid component state");
  let payload: SleepStageBreakdown | null = null;
  if (p.schema === "neoma.sleep-stage-breakdown/2") {
    if (p.type !== "sleep_stage_breakdown" || p.unit !== "percent" || p.denominator !== "total_sleep_time" ||
      p.initial_representation !== "pie" || JSON.stringify(p.representations) !== '["pie","bar"]' ||
      !["available", "unavailable"].includes(String(p.status)) || !Array.isArray(p.stages) ||
      Object.keys(p).some(key => !["schema", "type", "status", "reason", "unit", "denominator", "total_sleep_minutes", "stages", "representations", "initial_representation", "fallback"].includes(key))) throw new TypeError("Invalid component payload");
    str(p.fallback);
    if (p.status === "available") {
      const total = num(p.total_sleep_minutes);
      if (total <= 0 || p.reason !== null || p.stages.length !== 4) throw new TypeError("Invalid chart denominator");
      let sum = 0;
      p.stages.forEach((item, i) => {
        const stage = obj(item), minutes = num(stage.minutes), percent = num(stage.percent);
        if (Object.keys(stage).some(key => !["stage", "minutes", "percent"].includes(key)) || stage.stage !== ["N1", "N2", "N3", "REM"][i] || minutes > total || percent > 100 || Math.abs(percent - 100 * (minutes / total)) > 1e-10) throw new TypeError("Invalid chart stage");
        sum += minutes;
      });
      if (Math.abs(sum - total) > Math.max(1e-9, total * 1e-12)) throw new TypeError("Invalid chart total");
    } else if (p.stages.length || p.total_sleep_minutes !== null || !str(p.reason)) throw new TypeError("Invalid unavailable chart");
    payload = p as unknown as SleepStageBreakdown;
  }
  return { id: str(v.id), conversation_id: str(v.conversation_id), message_id: str(v.message_id), request_id: str(v.request_id),
    schema: str(p.schema), payload, state: { representation: s.representation as ChartRepresentation, revision: Number(s.revision) } };
}
